import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarIcon,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Eye,
  Clock,
  DollarSign,
  User,
  ChevronsUpDown,
  Check,
  X,
  Edit,
  Copy,
  Trash2,
  LogIn,
  LogOut,
  Loader2,
  Plus,
} from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import {
  format,
  isPast,
  startOfDay,
  isSameDay,
  eachDayOfInterval,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  startOfWeek,
  endOfWeek,
  parseISO,
} from "date-fns";
import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import MainLayout from "@/components/layouts";
import { useDailyWorkCalendar } from "@/hooks/useDailyWorkCalendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { MonthPicker } from "@/components/ui/month-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";
import { deleteDailyWorkDay, listDailyWorkExecutionsByDay } from "./daily-work.api";
import { daily_work_day_status } from "@/models/daily-work.model";
import { Badge } from "@/components/ui/badge";
import DayConfigurationModal from "./components/DayConfigurationModal";
import { toast } from "sonner";

type CalendarView = "day" | "week";

const CALENDAR_VIEW_STORAGE_KEY = "daily-work-calendar-view";

const getStatusColor = (day: DailyWorkDayJoin) => {
  switch (day.status) {
    case daily_work_day_status.pending:
      return "bg-gray-100 text-gray-800 border-gray-200";
    case daily_work_day_status.assigned:
      return "bg-blue-100 text-blue-800 border-blue-200";
    case daily_work_day_status.accepted:
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case daily_work_day_status.inprogress:
      return "bg-orange-100 text-orange-800 border-orange-200";
    case daily_work_day_status.completed:
      return "bg-green-100 text-green-800 border-green-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const statusBadgeConfig: Record<daily_work_day_status, { className: string; label: string }> = {
  [daily_work_day_status.pending]: { className: "bg-gray-100 text-gray-700", label: "Pending" },
  [daily_work_day_status.assigned]: { className: "bg-blue-100 text-blue-700", label: "Assigned" },
  [daily_work_day_status.accepted]: { className: "bg-yellow-100 text-yellow-700", label: "Accepted" },
  [daily_work_day_status.inprogress]: { className: "bg-orange-100 text-orange-700", label: "In Progress" },
  [daily_work_day_status.completed]: { className: "bg-green-100 text-green-700", label: "Completed" },
};

const getAssigneeName = (day: DailyWorkDayJoin) => {
  if (day.assigned_org) return day.assigned_org.display_name;
  if (day.assigned_user) return day.assigned_user.name;
  return "Unassigned";
};

const getPayment = (day: DailyWorkDayJoin) => {
  if (day.assigned_org && day.subs_payment != null) return day.subs_payment;
  return day.individual_payment;
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
};

const formatTime = (time: string | null) => {
  if (!time) return null;
  // time is HH:MM:SS format
  const [h, m] = time.split(":");
  const hour = parseInt(h);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${m} ${ampm}`;
};

function DayDetailPopover({
  day,
  onEdit,
  onCopy,
  onDelete,
}: {
  day: DailyWorkDayJoin;
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
}) {
  const [executions, setExecutions] = useState<DailyWorkExecutionJoin[]>([]);
  const [loadingExec, setLoadingExec] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingExec(true);
    listDailyWorkExecutionsByDay(day.id)
      .then((data) => {
        if (!cancelled) setExecutions(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingExec(false);
      });
    return () => { cancelled = true; };
  }, [day.id]);

  const latestExecution = executions.length > 0 ? executions[0] : null;

  return (
    <div className="space-y-3 p-1 min-w-[250px]">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="font-semibold text-sm">{day.daily_work_data.property.name}</p>
          {day.daily_work_data.property.address && (
            <p className="text-xs text-muted-foreground">{day.daily_work_data.property.address}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {day.recurring && <Badge variant="outline">Recurring</Badge>}
          <Badge variant="secondary" className={statusBadgeConfig[day.status]?.className}>
            {statusBadgeConfig[day.status]?.label || day.status}
          </Badge>
        </div>
      </div>
      <div>
        <p className="text-sm">
          {day.service.parent_name ? `${day.service.parent_name} > ` : ""}
          {day.service.name}
        </p>
        {day.duration_label && <p className="text-xs text-muted-foreground">{day.duration_label}</p>}
        {day.instructions && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap">{day.instructions}</p>
        )}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <User className="h-3.5 w-3.5 text-muted-foreground" />
        <span>{getAssigneeName(day)}</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
        <span>{formatCurrency(getPayment(day))}</span>
      </div>
      {day.max_hours != null && (
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
          <span>{day.max_hours} hrs max</span>
        </div>
      )}


      {/* Clock In/Out */}
      {loadingExec ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading execution...
        </div>
      ) : latestExecution ? (
        <div className="flex items-center gap-3 text-xs">
          {latestExecution.clock_in && (
            <div className="flex items-center gap-1 text-green-700">
              <LogIn className="h-3 w-3" />
              <span>{formatTime(latestExecution.clock_in)}</span>
            </div>
          )}
          {latestExecution.clock_out && (
            <div className="flex items-center gap-1 text-red-700">
              <LogOut className="h-3 w-3" />
              <span>{formatTime(latestExecution.clock_out)}</span>
            </div>
          )}
          {!latestExecution.clock_in && !latestExecution.clock_out && (
            <span className="text-muted-foreground">No on-site / off-site times</span>
          )}
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex gap-1.5 pt-2 border-t">
        <Link to={`/dailywork/${day.daily_work_data.id}`} className="flex-1">
          <Button variant="outline" size="sm" className="w-full">
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            View
          </Button>
        </Link>
        <Button variant="outline" size="sm" className="flex-1" onClick={onEdit}>
          <Edit className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={onCopy}>
          <Copy className="h-3.5 w-3.5 mr-1.5" />
          Copy
        </Button>
        <Button variant="destructive" size="sm" className="px-2" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

export default function DailyWorkCalendarPage() {
  const [view, setView] = useState<CalendarView>(() => {
    const saved = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return (saved as CalendarView) === "day" ? "day" : "week";
  });

  useEffect(() => {
    localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
  }, [view]);

  const currentMonth = moment().toDate();
  const [currentViewDate, setCurrentViewDate] = useState(currentMonth);

  const {
    selectedDate,
    setSelectedDate,
    allDays,
    daysForSelectedDate,
    loading,
    error,
    getDaysForDate,
    getDayCount,
    loadDays,
  } = useDailyWorkCalendar({ currentMonth: currentViewDate });

  // Filters
  const [propertyFilter, setPropertyFilter] = useState<string>("");
  const [assigneeFilter, setAssigneeFilter] = useState<string>("");
  const [propertyOpen, setPropertyOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);

  // Edit/Copy/Delete state
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState<DailyWorkDayJoin | null>(null);
  const [copyDay, setCopyDay] = useState<DailyWorkDayJoin | null>(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [dayToDelete, setDayToDelete] = useState<DailyWorkDayJoin | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleAddDay = () => {
    setIsAddMode(true);
    setSelectedDay(null);
    setCopyDay(null);
    setIsCopyMode(false);
    setDayModalOpen(true);
  };

  const handleEditDay = (day: DailyWorkDayJoin) => {
    setSelectedDay(day);
    setIsCopyMode(false);
    setIsAddMode(false);
    setCopyDay(null);
    setDayModalOpen(true);
  };

  const handleCopyDay = (day: DailyWorkDayJoin) => {
    setCopyDay(day);
    setIsCopyMode(true);
    setIsAddMode(false);
    setSelectedDay(null);
    setDayModalOpen(true);
  };

  const handleDeleteDay = (day: DailyWorkDayJoin) => {
    setDayToDelete(day);
    setDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!dayToDelete) return;
    setDeleting(true);
    try {
      await deleteDailyWorkDay(dayToDelete.id);
      toast.success("Day configuration deleted");
      setDeleteConfirm(false);
      setDayToDelete(null);
      loadDays();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const handleDayConfigSuccess = () => {
    setDayModalOpen(false);
    setIsCopyMode(false);
    setIsAddMode(false);
    setCopyDay(null);
    setSelectedDay(null);
    loadDays();
  };

  const propertyOptions = useMemo(() => {
    const map = new Map<string, string>();
    allDays.forEach((day) => {
      const prop = day.daily_work_data.property;
      if (prop.id && !map.has(prop.id)) {
        map.set(prop.id, prop.name);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allDays]);

  const assigneeOptions = useMemo(() => {
    const map = new Map<string, string>();
    allDays.forEach((day) => {
      if (day.assigned_org?.id) {
        const key = `org:${day.assigned_org.id}`;
        if (!map.has(key)) map.set(key, day.assigned_org.display_name || "Unknown Org");
      } else if (day.assigned_user?.id) {
        const key = `user:${day.assigned_user.id}`;
        if (!map.has(key)) map.set(key, day.assigned_user.name || "Unknown User");
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ value: id, label: name }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allDays]);

  const filterDay = useCallback(
    (day: DailyWorkDayJoin) => {
      if (propertyFilter && day.daily_work_data.property.id !== propertyFilter) return false;
      if (assigneeFilter) {
        if (assigneeFilter === "unassigned") {
          if (day.assigned_org || day.assigned_user) return false;
        } else {
          const isOrg = assigneeFilter.startsWith("org:");
          const id = assigneeFilter.split(":")[1];
          if (isOrg && day.assigned_org?.id !== id) return false;
          if (!isOrg && day.assigned_user?.id !== id) return false;
        }
      }
      return true;
    },
    [propertyFilter, assigneeFilter]
  );

  const getFilteredDaysForDate = useCallback(
    (date: Date): DailyWorkDayJoin[] => {
      return getDaysForDate(date).filter(filterDay);
    },
    [getDaysForDate, filterDay]
  );

  // Navigation
  const handlePrevious = () => {
    if (view === "day") {
      setCurrentViewDate(subDays(currentViewDate, 1));
      setSelectedDate(subDays(selectedDate, 1));
    } else {
      const newDate = subWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    }
  };

  const handleNext = () => {
    if (view === "day") {
      setCurrentViewDate(addDays(currentViewDate, 1));
      setSelectedDate(addDays(selectedDate, 1));
    } else {
      const newDate = addWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setCurrentViewDate(date);
    setSelectedDate(date);
  };

  const handleMonthYearSelect = (date: Date | undefined) => {
    if (!date) return;
    const weekStart = startOfWeek(date, { weekStartsOn: 1 });
    setCurrentViewDate(weekStart);
    setSelectedDate(weekStart);
  };

  const getWeekDays = () => {
    const weekStart = startOfWeek(currentViewDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentViewDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  };

  const getTitle = () => {
    if (view === "day") {
      return format(selectedDate, "EEEE, MMMM d, yyyy");
    }
    return format(currentViewDate, "MMMM yyyy");
  };

  // Day View - similar to weekly view but with detail rows
  const renderDayView = () => {
    const dayEntries = getFilteredDaysForDate(selectedDate);

    return (
      <div className="space-y-1">
        {dayEntries.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No daily work for {format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
          </div>
        ) : (
          dayEntries.map((day) => (
            <div
              key={day.id}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-colors hover:bg-muted/50",
                getStatusColor(day)
              )}
            >
              <div className="flex-1 min-w-0 flex items-center gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{day.daily_work_data.property.name}</p>
                  {day.daily_work_data.property.address && (
                    <p className="text-xs opacity-70 truncate">{day.daily_work_data.property.address}</p>
                  )}
                </div>
                <div className="hidden sm:block min-w-0 flex-1">
                  <p className="text-xs opacity-70">Service</p>
                  <p className="text-sm font-medium truncate">
                    {day.service.parent_name ? `${day.service.parent_name} > ` : ""}
                    {day.service.name}
                  </p>
                </div>
                <div className="hidden md:flex items-center gap-1.5 min-w-[140px]">
                  <User className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                  <span className="text-sm truncate">{getAssigneeName(day)}</span>
                </div>
                <div className="hidden lg:flex items-center gap-1.5 min-w-[100px]">
                  <DollarSign className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                  <span className="text-sm font-semibold">{formatCurrency(getPayment(day))}</span>
                </div>
                {day.max_hours != null && (
                  <div className="hidden lg:flex items-center gap-1.5 min-w-[80px]">
                    <Clock className="h-3.5 w-3.5 opacity-70 flex-shrink-0" />
                    <span className="text-sm">{day.max_hours} hrs</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Link to={`/dailywork/${day.daily_work_data.id}`}>
                  <Button variant="outline" size="icon" className="h-8 w-8 bg-white/50 hover:bg-white">
                    <Eye className="h-4 w-4" />
                  </Button>
                </Link>
                <Button variant="outline" size="icon" className="h-8 w-8 bg-white/50 hover:bg-white" onClick={() => handleEditDay(day)}>
                  <Edit className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8 bg-white/50 hover:bg-white" onClick={() => handleCopyDay(day)}>
                  <Copy className="h-4 w-4" />
                </Button>
                <Button variant="destructive" size="icon" className="h-8 w-8" onClick={() => handleDeleteDay(day)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // Week View
  const renderWeekView = () => {
    const weekDays = getWeekDays();

    return (
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day) => {
          const dayEntries = getFilteredDaysForDate(day);
          const entryCount = dayEntries.length;
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, moment().toDate());
          const isPastDate = isPast(startOfDay(day)) && !isToday;

          return (
            <div
              key={day.toISOString()}
              className={cn(
                "min-h-[300px] border border-border rounded-lg p-1 cursor-pointer transition-colors hover:bg-muted/50",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary/70",
                isToday && !isSelected && "bg-muted",
                isPastDate && !isSelected && "opacity-50"
              )}
              onClick={() => setSelectedDate(day)}
            >
              <div className="flex flex-col h-full">
                <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                {entryCount > 0 && (
                  <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                    {dayEntries.map((entry) => (
                      <Popover key={entry.id}>
                        <PopoverTrigger asChild>
                          <div
                            className={cn(
                              "text-xs px-1.5 py-1 rounded cursor-pointer hover:opacity-80 leading-tight",
                              getStatusColor(entry)
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <span className="block truncate font-medium">{entry.daily_work_data.property.name}</span>
                            <span className="block truncate opacity-80">{getAssigneeName(entry)} - {formatCurrency(getPayment(entry))}</span>
                            {entry.max_hours != null && (
                              <span className="block truncate opacity-70">{entry.max_hours} hrs</span>
                            )}
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto" align="start" side="right">
                          <DayDetailPopover
                            day={entry}
                            onEdit={() => handleEditDay(entry)}
                            onCopy={() => handleCopyDay(entry)}
                            onDelete={() => handleDeleteDay(entry)}
                          />
                        </PopoverContent>
                      </Popover>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const pageHeaderButtons = (
    <div className="flex items-center gap-2">
      <Button size="lg" onClick={handleAddDay}>
        <Plus className="mr-2 h-4 w-4" />
        Add Day
      </Button>
      <Link to="/dailywork/contracts">
        <Button variant="outline" size="lg">
          <CalendarIcon className="mr-2 h-4 w-4" />
          List View
        </Button>
      </Link>
    </div>
  );

  if (loading) {
    return (
      <MainLayout
        pageHeader={{
          title: "Daily Work Calendar",
          subtitle: "View daily work schedule",
          additionalButtons: pageHeaderButtons,
        }}
      >
        <div className="space-y-6 container mx-auto">
          <Card>
            <CardHeader>
              <CardTitle>Loading...</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-96 flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout
        pageHeader={{
          title: "Daily Work Calendar",
          subtitle: "View daily work schedule",
          additionalButtons: pageHeaderButtons,
        }}
      >
        <Card>
          <CardContent className="flex items-center justify-center h-96">
            <div className="text-center">
              <p className="text-red-500 mb-4">Error loading daily work: {error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  // For the DayConfigurationModal, we need to find the dailyWork for the selected day
  const modalDay = selectedDay || copyDay;
  const modalDailyWork = modalDay?.daily_work_data || null;

  return (
    <MainLayout
      pageHeader={{
        title: "Daily Work Calendar",
        subtitle: "View daily work schedule",
        additionalButtons: pageHeaderButtons,
      }}
    >
      <div className="space-y-6 container mx-auto">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 font-semibold text-lg hover:bg-transparent"
                    title={view === "day" ? format(selectedDate, "PPP") : format(currentViewDate, "MMM yyyy")}
                  >
                    <div className="flex items-center gap-2">
                      <CardTitle className="mb-0">{getTitle()}</CardTitle>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="bottom">
                  {view === "day" ? (
                    <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus />
                  ) : (
                    <MonthPicker selectedMonth={currentViewDate} onMonthSelect={handleMonthYearSelect} />
                  )}
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Popover open={propertyOpen} onOpenChange={setPropertyOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={propertyOpen}
                        className={cn("w-[180px] justify-between font-normal", !propertyFilter && "text-muted-foreground", propertyFilter && "pr-8")}
                      >
                        {propertyFilter ? propertyOptions.find((o) => o.value === propertyFilter)?.label : "All Properties"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search properties..." />
                        <CommandList>
                          <CommandEmpty>No properties found.</CommandEmpty>
                          <CommandGroup>
                            {propertyOptions.map((opt) => (
                              <CommandItem
                                key={opt.value}
                                value={opt.label}
                                onSelect={() => {
                                  setPropertyFilter(opt.value === propertyFilter ? "" : opt.value);
                                  setPropertyOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", propertyFilter === opt.value ? "opacity-100" : "opacity-0")} />
                                {opt.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {propertyFilter && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-8 top-0 h-full w-8 hover:bg-transparent z-10"
                      onClick={(e) => { e.stopPropagation(); setPropertyFilter(""); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <div className="relative">
                  <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={assigneeOpen}
                        className={cn("w-[180px] justify-between font-normal", !assigneeFilter && "text-muted-foreground", assigneeFilter && "pr-8")}
                      >
                        {assigneeFilter === "unassigned"
                          ? "Unassigned"
                          : assigneeFilter
                            ? assigneeOptions.find((o) => o.value === assigneeFilter)?.label
                            : "All Assignees"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search assignees..." />
                        <CommandList>
                          <CommandEmpty>No assignees found.</CommandEmpty>
                          <CommandGroup>
                            <CommandItem
                              value="Unassigned"
                              onSelect={() => {
                                setAssigneeFilter(assigneeFilter === "unassigned" ? "" : "unassigned");
                                setAssigneeOpen(false);
                              }}
                            >
                              <Check className={cn("mr-2 h-4 w-4", assigneeFilter === "unassigned" ? "opacity-100" : "opacity-0")} />
                              Unassigned
                            </CommandItem>
                            {assigneeOptions.map((opt) => (
                              <CommandItem
                                key={opt.value}
                                value={opt.label}
                                onSelect={() => {
                                  setAssigneeFilter(opt.value === assigneeFilter ? "" : opt.value);
                                  setAssigneeOpen(false);
                                }}
                              >
                                <Check className={cn("mr-2 h-4 w-4", assigneeFilter === opt.value ? "opacity-100" : "opacity-0")} />
                                {opt.label}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {assigneeFilter && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-8 top-0 h-full w-8 hover:bg-transparent z-10"
                      onClick={(e) => { e.stopPropagation(); setAssigneeFilter(""); }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={handlePrevious}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Select value={view} onValueChange={(value) => setView(value as CalendarView)}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {view === "day" && renderDayView()}
            {view === "week" && (
              <>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>
                {renderWeekView()}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit/Copy/Add Modal */}
      {dayModalOpen && (isAddMode || modalDailyWork) && (
        <DayConfigurationModal
          open={dayModalOpen}
          onOpenChange={(open) => {
            setDayModalOpen(open);
            if (!open) {
              setIsCopyMode(false);
              setIsAddMode(false);
              setCopyDay(null);
              setSelectedDay(null);
            }
          }}
          dailyWork={isAddMode ? null : modalDailyWork}
          workDate={modalDay ? modalDay.work_date : format(selectedDate, "yyyy-MM-dd")}
          existingDay={isCopyMode || isAddMode ? null : selectedDay}
          copyDay={isCopyMode ? copyDay : null}
          onSuccess={handleDayConfigSuccess}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this day configuration?</AlertDialogTitle>
            <AlertDialogDescription>
              {dayToDelete?.recurring
                ? "This date will be skipped by the Master Schedule — it will not be regenerated. This action cannot be undone."
                : "This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              onClick={confirmDelete}
            >
              {deleting ? "Deleting..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
